import React from "react";
import GuidePanel from "../GuidePanel.js";
import {
  GuideLead,
  GuideCallout,
  GuideSectionLabel,
  GuideCard,
  GuideCardGrid,
  GuideBlockList,
} from "../GuideBlocks.js";

const DecisionFatigueSection = ({ heading, accentIndex = 0, getVisual, getAccent }) => (
  <GuidePanel
    sectionId="decision-fatigue"
    heading={heading}
    accentIndex={accentIndex}
    getVisual={getVisual}
    getAccent={getAccent}
  >
    <GuideLead>
      By evening, &quot;what should we have for dinner&quot; can feel like the hardest question of your day.
      That is not because the decision is hard. It is because you have made hundreds of others before
      it.
    </GuideLead>
    <p className="guide-panel__p">
      <strong>Decision fatigue</strong> is the decline in decision quality after a long stretch of
      choosing. It is not about intelligence or willpower — it is about a finite mental resource
      getting used up.
    </p>
    <GuideSectionLabel>Why it happens</GuideSectionLabel>
    <p className="guide-panel__p">
      Every decision, even small ones — which email to answer first, what to wear, whether to speak
      up in a meeting — draws on the same limited pool of mental energy. The mind does not cleanly
      separate &quot;important&quot; from &quot;trivial&quot; when it comes to cost. It just spends.
    </p>
    <GuideBlockList
      items={[
        "You start defaulting to the easiest option, not the best one.",
        "Small decisions start to feel disproportionately heavy.",
      ]}
    />
    <p className="guide-panel__p">
      That is why the same question can feel manageable in the morning and unbearable late at night.
    </p>
    <GuideSectionLabel>The EIER lens on decision fatigue</GuideSectionLabel>
    <p className="guide-panel__p">
      Ahavibes&apos; EIER framework — <strong>Event → Interpretation → Evidence → Response</strong> —
      shows why fatigue distorts decisions specifically at the Interpretation step.
    </p>
    <p className="guide-panel__p">
      When mental resources are depleted, the mind reaches for the fastest interpretation available,
      not the most accurate one. A neutral email reads as passive-aggressive. A quiet partner reads
      as upset with you. You are not responding to what happened — you are responding to a rushed,
      under-resourced interpretation of what happened.
    </p>
    <GuideCallout>
      Recognizing where fatigue enters your thinking is the first step to interrupting it.
    </GuideCallout>
    <GuideSectionLabel>Reducing decision fatigue</GuideSectionLabel>
    <GuideCardGrid columns={2}>
      <GuideCard
        iconKey="thinking"
        title="Front-load hard decisions"
        description="Make the ones that matter most earlier in the day, when Interpretation is less likely to be distorted."
      />
      <GuideCard
        iconKey="clarity"
        title="Reduce the decision count"
        description="Standardize small choices so they do not compete for the same resource as decisions that matter."
      />
      <GuideCard
        iconKey="awareness"
        title="Name the fatigue"
        description='Noticing "I am deciding this from a depleted state" can slow Interpretation and prevent a reactive Response.'
      />
      <GuideCard
        iconKey="energy"
        title="Delay non-urgent decisions"
        description="If it can wait until tomorrow morning, let it."
      />
    </GuideCardGrid>
    <GuideCallout variant="amber">
      Decision fatigue is not a flaw to fix. It is a signal to build structure around.
    </GuideCallout>
  </GuidePanel>
);

export default DecisionFatigueSection;
